import { useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { getConfig } from '../../../config/runtimeConfig';
import { dataSourceRoot } from '../../../data/dataSourceFactory';
import type { ImageKind, ImageType } from '../../../data/imageTypes';
import { ApiError, call } from '../../collections/api';

/**
 * The study navigator (GET /studies/:studyId/navigator): the study's published
 * images in their saved order — or, until anybody has placed them (`placed`),
 * in no order of anybody's, and the page shows each group by name (sections.ts
 * orderByName) — with what the cards and group titles show and
 * whether the caller may reorder them. Through a share link the same shape is
 * read under the link's namespace (`base` = `<apiRoot>/s/<shareId>`), where
 * `editable` is always false.
 *
 * A signed-in reader also hears what they have seen (server http/seen.ts):
 * `seen_tracked`, and each image's `seen_utc`. Through a share link neither is
 * sent (a link visitor has no identity), so the page shows no seen marks.
 */
export interface Coded {
  code: string;
  meaning: string;
}

export interface NavigatorImage {
  /** `img_…` */
  image_id: string;
  series_instance_uid: string;
  /** A slide's pyramid, a flat picture, or a document (a PDF, opened as one). */
  kind: ImageKind;
  /** null: none of the seven ("Other", shown last). */
  type: ImageType | null;
  /** The description, else (members only) the filename; null shows a fallback. */
  name: string | null;
  stain: Coded | null;
  site: Coded | null;
  /** The code is one of the three CID 244 codes (data/imageTypes.ts). */
  laterality: Coded | null;
  /** Enterprise members only; null for everyone else. */
  specimen_id: string | null;
  /**
   * Its number: its place in the study as shown, 1…n through every section
   * and group (sections.ts buildSections), so it follows a drag. Not the
   * server's: 0 until the navigator lays the list out.
   */
  ordinal: number;
  /** How many its group holds — what its number is out of ("5 of 7"); 0 with the ordinal until laid out. */
  of: number;
  /** When this person first saw it; null not yet; absent where nobody is kept track of (a share link, the tail). */
  seen_utc?: string | null;
}

export interface StudyNavigator {
  editable: boolean;
  /** Whether the reader's seen marks are kept: a signed-in reader, never a share link. */
  seen_tracked: boolean;
  /** Whether anybody placed the images (a drag): then `images` is the order to show; else each group goes by name. */
  placed: boolean;
  images: NavigatorImage[];
}

/** What the page works from when there is no navigator to read: today's list, nothing to reorder, no marks. */
export const EMPTY_NAVIGATOR: StudyNavigator = { editable: false, seen_tracked: false, placed: false, images: [] };

/**
 * Keyed like the study's other DICOMweb reads (root, then the study UID third)
 * so a details save or a publish refetches it with them — see PER_STUDY_KEYS
 * in features/details/freshness.ts.
 */
export const navigatorKey = (studyUid: string | undefined, studyId: string | undefined) =>
  ['navigator', dataSourceRoot(), studyUid, studyId] as const;

/**
 * A study with no navigator to read — refused (401, 403) or not found here
 * (404: a study outside a share link, /dev with no server behind it) — is an
 * answer: nothing to section, nothing to reorder, and the page shows the plain
 * list it always did. A failure to reach the server (a network error, a 5xx)
 * is not: it throws, so the query retries and a blip is not kept as an empty
 * list; the page falls back to the plain list only once it has failed.
 */
export async function fetchNavigator(base: string, studyId: string): Promise<StudyNavigator> {
  const res = await fetch(`${base}/studies/${encodeURIComponent(studyId)}/navigator`, { credentials: 'include' });
  if (res.status === 401 || res.status === 403 || res.status === 404) return EMPTY_NAVIGATOR;
  if (!res.ok) throw new ApiError(`Request failed (${res.status})`, res.status);
  const body = (await res.json().catch(() => ({}))) as Partial<StudyNavigator>;
  const images = Array.isArray(body.images) ? body.images.map((img) => ({ ...img, ordinal: 0, of: 0 })) : [];
  return { editable: body.editable === true, seen_tracked: body.seen_tracked === true, placed: body.placed === true, images };
}

const navigatorQuery = (base: string, studyUid: string | undefined, studyId: string | undefined) => ({
  queryKey: navigatorKey(studyUid, studyId),
  queryFn: () => fetchNavigator(base, studyId!),
  enabled: !!studyUid && !!studyId,
});

/** `base` is the API root, or a share link's namespace under it. */
export function useNavigator(studyUid: string | undefined, studyId: string | undefined, base: string = getConfig().apiRoot) {
  return useQuery(navigatorQuery(base, studyUid, studyId));
}

/** Started beside the DICOMweb binding, so it is in flight with QIDO rather than after it. */
export function prefetchNavigator(qc: QueryClient, base: string, studyUid: string, studyId: string): Promise<void> {
  return qc.prefetchQuery(navigatorQuery(base, studyUid, studyId));
}

/**
 * The study's images placed in this order (PUT /studies/:id/order): the ids
 * the page shows, top to bottom; every published image takes a position,
 * numbered within its group, one the page did not list after the ones it did.
 */
export const placeImages = async (studyId: string, imageIds: string[]): Promise<void> => {
  let res: Response;
  try {
    res = await fetch(`${getConfig().apiRoot}/studies/${encodeURIComponent(studyId)}/order`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ images: imageIds }),
    });
  } catch {
    throw new ApiError('Could not reach the server. Check your connection and try again.');
  }
  if (res.ok) return;
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  throw new ApiError(body.error || `Request failed (${res.status})`, res.status);
};

/**
 * A reorder as the page makes it: the whole order the page shows after the
 * drop, drawn at once — and the study placed, so that order is the saved one
 * from here on — put back (and the person told) when the server refuses, and
 * the saved order read again either way.
 */
export function useReorder(key: ReturnType<typeof navigatorKey>, notify: (message: string) => void) {
  const qc = useQueryClient();
  const studyId = key[3];
  const m = useMutation({
    mutationFn: (order: NavigatorImage[]) => {
      if (!studyId) throw new ApiError('This study cannot be reordered here');
      return placeImages(studyId, order.map((img) => img.image_id));
    },
    onMutate: async (order) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<StudyNavigator>(key);
      if (prev) qc.setQueryData<StudyNavigator>(key, { ...prev, placed: true, images: order });
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      notify(e instanceof Error ? e.message : 'Could not move the image');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
  // Stable, so the page's own callback around it holds between renders.
  const { mutate } = m;
  return useCallback((order: NavigatorImage[]) => mutate(order), [mutate]);
}

/** The reader has seen this image (PUT /images/:id/seen): idempotent, the first look is the one kept. */
export const markSeen = (imageId: string): Promise<void> => call<void>(`/images/${encodeURIComponent(imageId)}/seen`, { method: 'PUT' });

/** Forget everything the reader saw in this study (DELETE /studies/:id/seen). */
export const resetSeen = (studyId: string): Promise<void> => call<void>(`/studies/${encodeURIComponent(studyId)}/seen`, { method: 'DELETE' });

/** The cached navigator with one image's mark set (a time) or taken off (null). */
const withSeen = (nav: StudyNavigator, imageId: string, seen: string | null): StudyNavigator => ({
  ...nav,
  images: nav.images.map((img) => (img.image_id === imageId ? { ...img, seen_utc: seen } : img)),
});

/**
 * An image as seen, the way the page marks one: the card's eye and the counts
 * change at once. The optimistic mark is the answer: nothing is read again for
 * every image opened, unless a read that crossed it (begun before the server
 * stored the mark) took it off again. A mark the server refuses is taken off
 * again quietly (a missed mark is not worth a banner) and the navigator read
 * afresh, except when the session is refused (401, 403): that read would come
 * back empty. Compare mode opens up to four at once, so an image already on
 * its way is not sent twice.
 */
export function useMarkSeen(key: ReturnType<typeof navigatorKey>) {
  const qc = useQueryClient();
  const sending = useRef(new Set<string>());
  const m = useMutation({
    mutationFn: (imageId: string) => markSeen(imageId),
    onMutate: async (imageId) => {
      await qc.cancelQueries({ queryKey: key });
      const nav = qc.getQueryData<StudyNavigator>(key);
      if (nav) qc.setQueryData<StudyNavigator>(key, withSeen(nav, imageId, new Date().toISOString()));
    },
    onSuccess: (_d, imageId) => {
      const nav = qc.getQueryData<StudyNavigator>(key);
      if (nav?.images.some((img) => img.image_id === imageId && !img.seen_utc)) void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e, imageId) => {
      const nav = qc.getQueryData<StudyNavigator>(key);
      if (nav) qc.setQueryData<StudyNavigator>(key, withSeen(nav, imageId, null));
      const refused = e instanceof ApiError && (e.status === 401 || e.status === 403);
      if (!refused) void qc.invalidateQueries({ queryKey: key });
    },
    onSettled: (_d, _e, imageId) => {
      sending.current.delete(imageId);
    },
  });
  return (imageId: string) => {
    if (sending.current.has(imageId)) return;
    sending.current.add(imageId);
    m.mutate(imageId);
  };
}

/**
 * A reset as the page makes it: every mark off at once, put back (and the
 * person told) when the server refuses, and the navigator read again either way.
 * Once it went through, `afterReset` runs: the page marks what is on screen
 * again, since the reader is looking at it (user, 2026-09-27).
 */
export function useResetSeen(key: ReturnType<typeof navigatorKey>, notify: (message: string) => void, afterReset?: () => void) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (studyId: string) => resetSeen(studyId),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<StudyNavigator>(key);
      if (prev) qc.setQueryData<StudyNavigator>(key, { ...prev, images: prev.images.map((img) => (img.seen_utc ? { ...img, seen_utc: null } : img)) });
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      notify(e instanceof Error && e.message ? e.message : 'Could not reset the seen marks');
    },
    // The read again starts first; a mark that follows cancels it and draws
    // itself over the cleared list (useMarkSeen), reading again if it crossed.
    onSettled: (_d, e) => {
      void qc.invalidateQueries({ queryKey: key });
      if (!e) afterReset?.();
    },
  });
  return (studyId: string) => m.mutate(studyId);
}

/**
 * Whether groups are told apart by specimen as well: enterprise, where the
 * navigator carries the specimen id. The community shell marks the document
 * (community/shell.ts); nothing else does.
 */
export const groupsBySpecimen = (): boolean => document.documentElement.dataset.edition !== 'community';
